import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckIcon, TransportIcon } from "./icons";
import { C, type Student, type FeeTerm, type Ledger, type Visit, type Checkpoint, type ParentWallet, type ChildTab, childColor } from "./tokens";
import { Avatar, Pill } from "./primitives";
import { fmtUGX } from "@/lib/format";
import { TriSpherePayTopUp } from "@/components/schoolos-pay-topup";
import {
  TransportTab,
  OverviewTab,
  AcademicsTab,
  AttendanceTab,
  FinanceTab,
  HealthTab,
  DocsTab,
  TimelineTab,
  MessagesTab,
} from "@/features/student-card-tabs";

export function ChildWorkspace({ child, tab, setTab, students, fees, ledger, visits, checkpoints, parentWallet, tabs }: {
  child: Student; tab: ChildTab; setTab: (t: ChildTab) => void;
  students: Student[]; fees: FeeTerm[]; ledger: Ledger[]; visits: Visit[]; checkpoints: Checkpoint[];
  parentWallet: ParentWallet | null | undefined;
  tabs: { k: ChildTab; label: string; icon: React.ComponentType<{ className?: string }> }[];
}) {
  const color = childColor(students, child.id);
  const childFee = fees.find(f => f.student_id === child.id);
  const outstanding = childFee ? Math.max(0, childFee.total_ugx - childFee.paid_ugx) : 0;

  const payTrigger = (
    <button
      className="w-full mt-6 py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2"
      style={{ background: "#1C1917" }}
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7V4h16v3M9 20h6M12 4v16" /></svg>
      Pay {fmtUGX(outstanding)} Now
    </button>
  );

  const INK = "#1C1917";
  const MUTED = "#8B6F47";
  const GOOD = "#8B6F47";

  return (
    <div className="space-y-5 max-w-6xl">
      {/* CHILD HEADER — disciplined palette, no truncation */}
      <div className="relative overflow-hidden rounded-[16px] border bg-white" style={{ borderColor: "#E2D6C0", boxShadow: "0 4px 24px -8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)" }}>
        <div className="h-px" style={{ background: "#E2D6C0" }} />
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              <Avatar name={child.full_name} color={color} size={72} />
              <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full border-2 border-white flex items-center justify-center" style={{ background: GOOD }}>
                <CheckIcon className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              {/* Name — wraps, no truncate */}
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight" style={{ color: INK, fontFamily: '"Playfair Display", Georgia, serif' }}>{child.full_name}</h2>
              <p className="text-sm mt-1 leading-snug" style={{ color: MUTED }}>{child.class_name ?? child.class_label}</p>
              <p className="text-xs mt-0.5 leading-snug" style={{ color: MUTED }}>{child.school_name ?? "School not set"}</p>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                <Pill color={INK}>{child.student_no}</Pill>
                {child.bus_route && <Pill color={INK}><TransportIcon className="h-3 w-3" />{child.bus_route.split(" - ")[0]}</Pill>}
              </div>
            </div>
            <div className="rounded-[14px] px-4 py-3 text-center shrink-0" style={{ background: `${GOOD}08`, border: `1px solid ${GOOD}18` }}>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>Wallet</p>
              <p className="text-sm font-bold mt-0.5" style={{ color: GOOD }}>{fmtUGX(child.balance_ugx)}</p>
            </div>
          </div>
        </div>
        {/* TAB BAR — unified ink palette */}
        <div className="border-t px-3 py-2.5 flex gap-1.5 overflow-x-auto no-scrollbar" style={{ borderColor: "#EDE4D4", background: "#FAFAF8" }}>
          {tabs.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} className="flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2 rounded-xl text-xs font-semibold transition-all" style={tab === t.k ? { background: INK, color: "#fff", boxShadow: "0 2px 8px rgba(11,42,31,0.2)" } : { color: MUTED, background: "transparent" }}>
              <t.icon className="h-3.5 w-3.5 shrink-0" />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* TAB CONTENT */}
      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
          {tab === "overview" && <OverviewTab studentId={child.id} onMessageTeacher={() => setTab("messages")} onPayFees={() => setTab("finance")} onViewTransport={() => setTab("transport")} onViewHealth={() => setTab("health")} onViewDocuments={() => setTab("documents")} />}
          {tab === "academics" && <AcademicsTab studentId={child.id} subjects={[]} assessments={[]} assignments={[]} />}
          {tab === "attendance" && <AttendanceTab studentId={child.id} />}
          {tab === "finance" && (
            <div>
              <FinanceTab studentId={child.id} onPayFees={undefined} />
              <TriSpherePayTopUp
                walletId={parentWallet?.id}
                students={[child]}
                fees={fees}
                trigger={payTrigger}
                presetStudentId={child.id}
                presetAmount={outstanding}
                presetBucket="fees"
                presetLabel={`${child.full_name} — School Fees`}
              />
            </div>
          )}
          {tab === "health" && <HealthTab studentId={child.id} />}
          {tab === "transport" && <TransportTab />}
          {tab === "messages" && <MessagesTab studentId={child.id} />}
          {tab === "documents" && <DocsTab studentId={child.id} />}
          {tab === "timeline" && <TimelineTab studentId={child.id} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
